<template>
  
  <div 
    ref         = "container"
    class       = "overlay-container"
  >

    <!-- Shapes -->
    <svg 
      ref                 = "shapes"
      viewBox             = "0 0 1 1"
      preserveAspectRatio = "none"
      @mousedown.left     = "onMouseDown($event, true)"
    >

      <!-- Existing Shapes -->

      <Shape
        v-for       = "(shape, index) of shapes"
        :key        = "index"
        :shape      = "shape"
        :canSelect  = "tool === 'selection'"

        @mousedown    = "onShapeMouseDown($event, shape)"
      >
      </Shape>

      <!-- New Shape -->

      <Shape 
        v-if    = "isDrawing && newShape"
        :shape  = "newShape"
      >
      </Shape>

      <!-- Selections -->
      <g>

        <SelectionBox
          v-for       = "(shape, index) of selectedShapes"
          :key        = "index"
          :shape      = "shape"
          @mousedown  = "onMouseDown($event, false);"
        >
        </SelectionBox>

      </g>

    </svg>

  </div>

</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import { 
  SET_ANNOTATION_TOOL,
  SET_ANNOTATION_SHAPES_SELECTED
} from '@/utils/mutation-types';
import { 
  ADD_SHAPE_TO_ANNOTATION,
  UPDATE_SHAPES_ON_ANNOTATION,
  PAUSE_VIDEO
} from '@/utils/action-types';
import Shape from './shapes/Shape';
import SelectionBox from './shapes/SelectionBox';
import { moveShape, toShape } from './utilities/shape-utilities';

// #endregion Imports

// #region Constants

const POINT_DISTANCE_CUTOFF = 15;

// #endregion Constants

export default {

  // #region Properties

  name:       'AnnotationOverlay',
  components: {
    SelectionBox,
    Shape
  },
  data() {
    return {
      containerWidth:  0,
      containerHeigth: 0,
      sizeObserver:    undefined,

      isDrawing: false,
      startX:    0,
      startY:    0,
      endX:      0,
      endY:      0,
      points:    []
    }
  },
  computed: {
    ...mapState([
      'annotations',
      'ui',
      'video',
    ]),
    currentFrame() {
      return this.video.currentFrame;
    },
    tool: {
      get() {
        return this.ui.annotationTool;
      },
      set(tool) {
        this.$store.commit(SET_ANNOTATION_TOOL, tool);

        if (tool !== 'selection') {
          this.selectedShapeIds = [];
        }
      }
    },
    selectedShapeIds: {
      get() {
        return this.ui.annotationShapesSelected;
      },
      set(shapeIds) {
        this.$store.commit(SET_ANNOTATION_SHAPES_SELECTED, shapeIds);
      }
    },
    newShape() {
      if (!this.isDrawing) {
        return undefined;
      }

      // All shapes live in a 0,0 - 1,1 square.
      const startX = this.startX / this.containerWidth;
      const endX = this.endX / this.containerWidth;

      const startY = this.startY / this.containerHeigth;
      const endY = this.endY / this.containerHeigth;

      return toShape(
        this.tool, 
        this.ui.annotationToolsColor, 
        startX, startY, endX, endY, 
        this.points.map(p => ({
          x: p.x / this.containerWidth, 
          y: p.y / this.containerHeigth
        }))
      );
    },
    shapes() {
      const annotations = this.annotations.annotations.filter(a => (
        (a.frame <= this.currentFrame) &&
        ((a.frame + a.duration) >= this.currentFrame)
      ));
      let shapes = [];

      annotations.forEach(a => shapes = shapes.concat(a.shapes));

      return shapes;
    },
    selectedShapes() {
      return this.shapes.filter(s => this.selectedShapeIds.includes(s.id));
    }
  },
  watch: {
    currentFrame() {
      this.selectedShapeIds = [];
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.onUpdateContainerDimensions();
    this.sizeObserver = new ResizeObserver(this.onUpdateContainerDimensions).observe(this.$refs.container);
  },

  beforeDestroy() {
    if (this.sizeObserver) {
      this.sizeObserver.unobserve(this.$refs.container);
    }
  },

  // #endregion Life-Cycle

  // #region Methods

  methods: {

    // #region Events

    onUpdateContainerDimensions() {
      if (this.$refs.container) {
        this.containerWidth = this.$refs.container.clientWidth;
        this.containerHeigth = this.$refs.container.clientHeight;
      } else {
        this.containerWidth = 0;
        this.containerHeigth = 0;
      }
    },

    onSelectTool(tool) {
      this.tool = tool;
    },

    onMouseDown(event, resetSelection) {
      if (!this.ui.areAnnotationToolsShown) {
        return;
      }

      event.stopPropagation();

      if (this.tool !== 'selection') {
        this.$store.dispatch(PAUSE_VIDEO);
      }

      if (resetSelection) {
        this.selectedShapeIds = [];
      }

      this.isDrawing = true;

      this.startX = event.offsetX;
      this.endX = this.startX;

      this.startY = event.offsetY;
      this.endY = this.startY;

      this.points = [{ x: this.startX, y: this.startY }];
      
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
    },

    onMouseMove(event) {
      if (!this.isDrawing) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      let shapesPosition = this.$refs.shapes.getBoundingClientRect();

      const previousX = this.endX;
      const previousY = this.endY;

      this.endX = Math.min(this.$refs.shapes.clientWidth, Math.max(0, event.clientX - shapesPosition.x));
      this.endY = Math.min(this.$refs.shapes.clientHeight, Math.max(0, event.clientY - shapesPosition.y));

      // See if we are moving shapes:
      if (this.selectedShapeIds.length > 0) {
        const dx = ((this.endX - previousX) / this.containerWidth);
        const dy = ((this.endY - previousY) / this.containerHeigth);

        this.selectedShapes.forEach(s => moveShape(s, dx, dy));
      }

      if (this.tool === 'polygon') {
        // Don't need to store all the points:
        const x = this.endX - this.points[this.points.length - 1].x;
        const y = this.endY - this.points[this.points.length - 1].y;
        const distance = Math.sqrt((x * x) + (y * y));

        if (distance > POINT_DISTANCE_CUTOFF) {
          this.points.push({ x: this.endX, y: this.endY });
        }
      }
    },

    onMouseUp() {
      if (!this.isDrawing) {
        return;
      }

      if (this.tool !== 'selection') {
        this.$store.dispatch(ADD_SHAPE_TO_ANNOTATION, this.newShape);
      } else {
        this.$store.dispatch(UPDATE_SHAPES_ON_ANNOTATION, this.selectedShapes);
      }

      this.isDrawing = false;

      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
    },

    onShapeMouseDown(event, shape) {
      if (this.tool !== 'selection') {
        return;
      }

      if (!event.ctrlKey) {
        this.selectedShapeIds = [];
      }

      this.selectedShapeIds.push(shape.id);

      this.onMouseDown(event, false);
    }

    // #endregion Events

  }

  // #endregion Methods

}

</script>

<style lang="scss" scoped>

  .overlay-container {
    position:         relative;
    display:          flex;
    align-items:      center;
    justify-content:  center;

    svg {
      width:  100%;
      height: 100%;
    }

  }

</style>
